import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AccountBlockedNotice from "../components/auth/AccountBlockedNotice";
import AuthDecor from "../components/auth/AuthDecor";
import AuthHero from "../components/auth/AuthHero";
import TimeplannedLogo from "../components/auth/TimeplannedLogo";
import { useAuthPageBrowserChrome } from "../components/auth/useAuthPageBrowserChrome";
import SignInForm from "../components/auth/SignInForm";
import RegisterForm from "../components/auth/RegisterForm";
import ResetPasswordForm from "../components/auth/ResetPasswordForm";
import PasswordlessSignInForm from "../components/auth/PasswordlessSignInForm";
import { ROUTES } from "../routes";

type AuthMode = "signIn" | "register" | "resetPassword" | "passwordless";

// Deep-aubergine base with a brand-purple glow rising from the top-left and a
// faint teal counter-glow bottom-right — the whole auth screen is deliberately
// dark in both themes.
const PAGE_BACKGROUND =
  "radial-gradient(110% 80% at 12% -8%, rgba(108, 13, 242, 0.34) 0%, rgba(108, 13, 242, 0.10) 38%, transparent 62%), " +
  "radial-gradient(90% 70% at 105% 112%, rgba(20, 184, 156, 0.10) 0%, transparent 55%), " +
  "linear-gradient(155deg, #221735 0%, #150f1e 46%, #0d0a12 100%)";

const GRAIN_TEXTURE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const LoginPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  useAuthPageBrowserChrome();
  // /register opens straight on the registration form — the marketing site's
  // trial CTAs land here, so a new visitor never sees a "welcome back" login.
  const [mode, setMode] = useState<AuthMode>(
    location.pathname === ROUTES.REGISTER ? "register" : "signIn"
  );

  useEffect(() => {
    if ((location.state as { register?: boolean })?.register) {
      setMode("register");
    }
  }, [location.state]);

  const blocked = new URLSearchParams(location.search).get("error") === "account_blocked";
  const year = new Date().getFullYear();

  return (
    // The `dark` wrapper forces every dark: variant inside, so the screen
    // renders identically whichever theme the app is in.
    <div
      className="dark relative min-h-screen overflow-hidden"
      style={{ background: PAGE_BACKGROUND }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: GRAIN_TEXTURE }}
      />
      <AuthDecor />

      <div className="relative flex min-h-screen flex-col px-6 py-6 sm:px-10 lg:px-16 lg:py-8 [@media(max-height:760px)]:py-3 [@media(max-height:760px)]:lg:py-3">
        {/* Frame the content on ultra-wide screens so the form never pins to
            the viewport edge; below ~1450px wide this changes nothing. */}
        <div className="mx-auto flex w-full max-w-[1320px] flex-1 flex-col">
        <header className="shrink-0">
          <Link to={ROUTES.LOGIN} aria-label="timeplanned">
            <TimeplannedLogo className="fill-white" />
          </Link>
        </header>

        <div className="flex flex-1 flex-col items-center justify-center gap-10 py-6 lg:flex-row lg:justify-between lg:gap-16 lg:py-8 [@media(max-height:760px)]:gap-6 [@media(max-height:760px)]:py-2 [@media(max-height:760px)]:lg:py-2">
          <AuthHero />

          <div className="w-full max-w-[520px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.25 }}
              >
                {mode === "resetPassword" ? (
                  <ResetPasswordForm onCancel={() => setMode("signIn")} />
                ) : mode === "register" ? (
                  <RegisterForm onSignIn={() => setMode("signIn")} />
                ) : mode === "passwordless" ? (
                  <PasswordlessSignInForm onBack={() => setMode("signIn")} />
                ) : (
                  <>
                    {blocked && <AccountBlockedNotice />}
                    <SignInForm
                      onRegister={() => setMode("register")}
                      onResetPassword={() => setMode("resetPassword")}
                      onPasswordless={() => setMode("passwordless")}
                    />
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <footer className="flex shrink-0 flex-col items-center justify-between gap-2 text-sm sm:flex-row">
          <p className="hidden text-grey-60 dark:text-grey-60 md:block">
            {t("auth.hero.point1")} · {t("auth.hero.point2")} ·{" "}
            {t("auth.hero.point3")}
          </p>
          <div className="flex items-center gap-5 text-grey-60 dark:text-grey-60">
            <span>© {year} timeplanned</span>
            <Link
              to={ROUTES.TERMS_OF_SERVICE}
              className="transition-colors hover:text-white"
            >
              {t("auth.termsOfService")}
            </Link>
            <Link
              to={ROUTES.PRIVACY_POLICY}
              className="transition-colors hover:text-white"
            >
              {t("auth.privacyPolicy")}
            </Link>
          </div>
        </footer>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
